import {
  getTagListsForVizportalApi,
  InlineEditorCancelButton,
  InlineEditorEditButton,
  InlineEditorSaveButton,
  isEditMode,
  isReadonlyMode,
  TagsEditor,
  TagsEditorState,
  useInlineEditor
} from '@tableau/vizportalclient-common-components';
import { TabStyles } from '@tableau/widgets';
import React from 'react';
import { Messages } from '../../../../../gen-src/Localize';
import {
  ApiRequestsContext,
  divWithStyle
} from '../../../data-catalog-shared';
import {
  ColumnAndFieldDetailsLabel,
  ColumnAndFieldDetailsProperty,
  ColumnAndFieldDetailsTableCell,
  ColumnAndFieldDetailsTagsEditorWrapper,
  ColumnAndFieldDetailsTextAreaWrapper,
  ColumnDetailsPlaceholder,
  descriptionMaxHeight,
  SaveAndCancelButtonsFlexWrapperSharedStyle,
  SaveAndCancelButtonsWrapper
} from '../style/ColumnAndFieldDetailsStyles';

export type TagEditorElement = {
  readonly element: JSX.Element;
  readonly startEditing: () => void;
}

export type ColumnInlineTagEditorProps = {
  readonly canEditTags: boolean;
  readonly columnLuid: string;
  readonly tags: ReadonlyArray<string>;
  readonly onUpdate: () => void;
  readonly viewOnly?: boolean;
};

// helper function to convert a tag array into a map of tagname and tagCount.
export function convertArrayToMap(tags: ReadonlyArray<string>): Map<string, number> {
  const tagMap = new Map<string, number>();
  if (tags) {
    tags.forEach(tag => {
      const originalTagCount = tagMap.get(tag) ?? 0;
      tagMap.set(tag, originalTagCount + 1);
    });
  }
  return tagMap;
}

export function getTagNamesFromMap(tags: Map<string, number>): ReadonlyArray<string> {
  return Array.from(tags.keys());
}

export function ColumnInlineTagEditor(props: ColumnInlineTagEditorProps): JSX.Element {
  const { element } = useColumnInlineTagEditor(props);
  return element;
}

export function useColumnInlineTagEditor(props: ColumnInlineTagEditorProps): TagEditorElement {
  const { columnLuid, onUpdate, viewOnly } = props;
  const { searchMatchingTags, updateColumnTags } = React.useContext(ApiRequestsContext);
  const [tags, setTags] = React.useState<ReadonlyArray<string>>(props.tags);

  const searchMatchingTagsWrapper = React.useCallback(
    (partialText: string) => searchMatchingTags({ partialText, maxItems: 5, startIndex: 0 }),
    [searchMatchingTags]
  );

  const onSave = React.useCallback(
    async (editorState: TagsEditorState) => {
      const { tagsToAdd, tagsToRemove } = getTagListsForVizportalApi({ oldTags: convertArrayToMap(tags), newTags: editorState.tags });

      if (tagsToAdd.length < 1 && tagsToRemove.length < 1) {
        return;
      }

      await updateColumnTags({ luids: [columnLuid], tagsToAdd, tagsToRemove });
      onUpdate();
      setTags(getTagNamesFromMap(editorState.tags));
    },
    [columnLuid, tags, onUpdate, updateColumnTags]
  );

  const logic = useInlineEditor<TagsEditorState>({
    editorStateWhenEnteringEditMode: { tags: convertArrayToMap(tags), suggestions: [], partialText: null },
    canEdit: props.canEditTags,
    canSaveFn: state => {
      const { tagsToAdd, tagsToRemove } = getTagListsForVizportalApi({ oldTags: convertArrayToMap(tags), newTags: state.tags });
      return tagsToAdd.length > 0 || tagsToRemove.length > 0;
    },
    onSave
  });

  return { element: (
    <ColumnAndFieldDetailsProperty>
      <ColumnAndFieldDetailsLabel>{Messages.Tags()}</ColumnAndFieldDetailsLabel>
      {
        isEditMode(logic)
          ? (
            <ColumnAndFieldDetailsTableCell>
              <ColumnAndFieldDetailsTextAreaWrapper>
                <ColumnAndFieldDetailsTagsEditorWrapper>
                  <TagsEditor
                    editorState={logic.editorState}
                    setEditorState={logic.setEditorStateWithinEditMode}
                    searchMatchingTags={searchMatchingTagsWrapper}
                    disableEditor={logic.isActionBusy}
                    style={{ height: '100%' }}
                    itemCount={1}
                  />
                </ColumnAndFieldDetailsTagsEditorWrapper>
              </ColumnAndFieldDetailsTextAreaWrapper>
              <SaveAndCancelButtonsWrapper>
                <SaveAndCancelButtonsFlexWrapper>
                  <div style={{ marginRight: TabStyles.Spacing.MediumSpace }}>
                    <InlineEditorCancelButton {...logic} />
                  </div>
                  <div>
                    <InlineEditorSaveButton {...logic} />
                  </div>
                </SaveAndCancelButtonsFlexWrapper>
              </SaveAndCancelButtonsWrapper>
            </ColumnAndFieldDetailsTableCell >
          ) : (
            <>
              <ColumnDetailsTagsValue>
                <ReadonlyTags savedTags={tags} />
              </ColumnDetailsTagsValue>
              {
                !viewOnly ?
                  <InlineEditorEditButton {...logic} editButtonLabel={Messages.EditTags()} /> : null
              }
            </>
          )
      }
    </ColumnAndFieldDetailsProperty>
  ),
    startEditing: () => {
      if (isReadonlyMode(logic)) {
        logic.enterEditMode();
      }
    }
  };
}

function ReadonlyTags(props: { savedTags: ReadonlyArray<string> }): JSX.Element {
  return props.savedTags.length > 0
    ? <div data-tb-test-id={'readonly-tags'} style={{ maxHeight: descriptionMaxHeight }}>{props.savedTags.join(', ')}</div>
    : <ColumnDetailsPlaceholder>{Messages.NoTagsAvailable()}</ColumnDetailsPlaceholder>;
}

export const ColumnDetailsTagsValue = divWithStyle('ColumnDetailsTagsValue', {
  display: 'table-cell',
  padding: TabStyles.Spacing.DefaultSpace,
  overflowY: 'auto',
  wordBreak: 'break-word',
});

const SaveAndCancelButtonsFlexWrapper = divWithStyle('SaveAndCancelButtonsFlexWrapper', {
  ...SaveAndCancelButtonsFlexWrapperSharedStyle,
  marginTop: TabStyles.Spacing.DefaultSpace,
});
